import axios from 'axios'
import type { Job, JobCreate, Memo, MemoDetail, UploadedFile } from '@/types'

const API_BASE = '/api/v1'

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
})

const ACCESS_TOKEN_KEY = 'access_token'

export const authToken = {
  get: () => {
    try {
      return localStorage.getItem(ACCESS_TOKEN_KEY)
    } catch {
      return null
    }
  },
  set: (token: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, token)
  },
  clear: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
  },
}

api.interceptors.request.use((config) => {
  const token = authToken.get()
  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export const authApi = {
  login: async (username: string, password: string) => {
    const response = await api.post<{ access_token: string; token_type: string }>('/auth/login', {
      username,
      password,
    })
    authToken.set(response.data.access_token)
    return response.data
  },
}

// Transform snake_case to camelCase for Job responses
const transformJob = (job: any): Job => ({
  id: job.id,
  type: job.type,
  customerId: job.customer_id,
  status: job.status,
  progress: job.progress,
  inputFiles: job.input_files || [],
  inputFileUrls: job.input_file_urls || [],
  result: job.result,
  error: job.error,
  createdAt: job.created_at,
  updatedAt: job.updated_at,
  completedAt: job.completed_at,
})

// Transform snake_case to camelCase for Memo responses
const transformMemo = (memo: any): Memo => ({
  id: memo.id,
  customerId: memo.customer_id,
  type: memo.type,
  title: memo.title,
  filename: memo.filename,
  path: memo.path,
  size: memo.size,
  createdAt: memo.created_at,
  assets: memo.assets || [],
  assetCount: memo.asset_count || 0,
})

const transformMemoDetail = (memo: any): MemoDetail => ({
  ...transformMemo(memo),
  content: memo.content || '',
  htmlContent: memo.html_content || '',
  analysisData: memo.analysis_data,
})

// Transform snake_case to camelCase for Upload responses
const transformUploadedFile = (file: any): UploadedFile => ({
  id: file.id,
  filename: file.filename,
  storedFilename: file.stored_filename,
  path: file.path,
  size: file.size,
  type: file.type,
  customerId: file.customer_id,
  uploadedAt: file.uploaded_at,
  url: file.url,
})

// Jobs API
export const jobsApi = {
  list: async (params?: {
    customerId?: string
    status?: string
    type?: string
    page?: number
    pageSize?: number
  }) => {
    const response = await api.get<{ jobs: any[]; total: number }>('/jobs', { params })
    return {
      jobs: response.data.jobs.map(transformJob),
      total: response.data.total,
    }
  },

  get: async (jobId: string) => {
    const response = await api.get<any>(`/jobs/${jobId}`)
    return transformJob(response.data)
  },

  create: async (data: JobCreate) => {
    // Convert to snake_case for backend
    const payload = {
      type: data.type,
      customer_id: data.customerId,
      input_files: data.inputFiles,
      input_file_urls: data.inputFileUrls || [],
    }
    const response = await api.post<any>('/jobs', payload)
    return transformJob(response.data)
  },

  cancel: async (jobId: string) => {
    const response = await api.post<Job>(`/jobs/${jobId}/cancel`)
    return response.data
  },
}

// Memos API
export const memosApi = {
  list: async (params?: { customerId?: string; page?: number; pageSize?: number }) => {
    const response = await api.get<{ memos: any[]; total: number }>('/memos', { params })
    return {
      memos: response.data.memos.map(transformMemo),
      total: response.data.total,
    }
  },

  get: async (memoId: string) => {
    const response = await api.get<any>(`/memos/db/${memoId}`)
    return transformMemoDetail(response.data)
  },

  getContent: async (memoId: string) => {
    const response = await api.get<{ content: string; html: string }>(`/memos/${memoId}/content`)
    return response.data
  },

  getAssets: async (memoId: string) => {
    const response = await api.get<{ assets: Array<{ name: string; path: string; type: string }> }>(
      `/memos/${memoId}/assets`
    )
    return response.data
  },
}

// Upload API
export const uploadApi = {
  uploadFile: async (
    file: File,
    customerId: string,
    fileType?: string,
    saveToCustomers: boolean = true
  ): Promise<UploadedFile> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('customer_id', customerId)
    if (fileType) {
      formData.append('file_type', fileType)
    }
    // Save directly to customers/ directory for analysis
    formData.append('save_to_customers', saveToCustomers ? 'true' : 'false')

    const response = await api.post<UploadedFile>('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return transformUploadedFile(response.data)
  },

  listFiles: async (customerId: string) => {
    const response = await api.get<{ files: any[]; total?: number }>(`/upload/${customerId}/files`)
    return {
      files: (response.data.files || []).map(transformUploadedFile),
      total: response.data.total,
    }
  },
}

// Customers API - get available customers with data
export interface AvailableCustomer {
  id: string
  name: string
  file_count: number
  csv_count: number
  json_count: number
  has_amazon_data: boolean
  has_brcs_data: boolean
}

export const customersApi = {
  list: async () => {
    const response = await api.get<{ customers: Array<{ id: string; name: string }> }>('/customers')
    return response.data
  },
  listAvailable: async (): Promise<{ customers: AvailableCustomer[]; total: number }> => {
    const response = await api.get<{ customers: AvailableCustomer[]; total: number }>('/customers/available')
    return response.data
  },
}

// Analysis API
export const analysisApi = {
  runAnalysis: async (data: {
    customerId: string
    analysisType: 'amazon' | 'brcs' | 'brig' | 'full'
    inputFiles: string[]
  }) => {
    const response = await api.post<Job>('/analyze', {
      customer_data: {
        customer_id: data.customerId,
      },
      analysis_type: data.analysisType,
      parameters: {
        input_files: data.inputFiles,
      },
    })
    return response.data
  },
}

// Health check
export const healthApi = {
  check: async () => {
    const response = await api.get('/health')
    return response.data
  },
}

// Dashboard Stats API
export interface DashboardStats {
  total_analyses: number
  active_customers: number
  pending_jobs: number
  avg_risk_score: number
  last_updated: string
}

export const statsApi = {
  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await api.get<DashboardStats>('/stats')
    return response.data
  },
}

export default api
